import { queryOptions } from '@tanstack/react-query'

import type { AuditLogListResponse } from '@/bindings/AuditLogListResponse'
import type { EntityType } from '@/bindings/EntityType'
import type { OrderHistoryResponse } from '@/bindings/OrderHistoryResponse'
import { request } from '@/lib/api'
import { type PageSearch, parsePageSearch } from '@/lib/page-search'
import { PAGE_SIZE } from '@/lib/paging'

/**
 * Which page a row can appear on, in filter order. `platform` rows carry no
 * restaurant id, so the restaurant page never lists them. The `Record` keeps
 * this exhaustive: a new backend enum value fails the build until placed here.
 */
const ENTITY_SCOPE: Record<EntityType, 'restaurant' | 'platform'> = {
  restaurant: 'restaurant',
  branch: 'restaurant',
  menu_category: 'restaurant',
  menu_item: 'restaurant',
  table: 'restaurant',
  account: 'restaurant',
  order: 'restaurant',
  session: 'restaurant',
  platform_user: 'platform',
  cleanup: 'platform',
}

export const ENTITY_TYPES = Object.keys(ENTITY_SCOPE) as readonly EntityType[]

/** The restaurant page's "What changed" options. */
export const RESTAURANT_ENTITY_TYPES: readonly EntityType[] = ENTITY_TYPES.filter((entity) => ENTITY_SCOPE[entity] === 'restaurant')

export function isEntityType(value: unknown): value is EntityType {
  return typeof value === 'string' && (ENTITY_TYPES as readonly string[]).includes(value)
}

const DATE = /^\d{4}-\d{2}-\d{2}$/

export interface AuditFilter {
  branch?: string
  entity?: EntityType
  /** `YYYY-MM-DD`, inclusive. */
  from?: string
  to?: string
}

export interface AuditSearch extends PageSearch, AuditFilter {}

/** Anything malformed is dropped rather than refused: a stale link still lands on the page. */
export function parseAuditSearch(search: Record<string, unknown>): AuditSearch {
  const out: AuditSearch = parsePageSearch(search)
  if (typeof search.branch === 'string' && search.branch) out.branch = search.branch
  if (isEntityType(search.entity) && RESTAURANT_ENTITY_TYPES.includes(search.entity)) out.entity = search.entity
  if (typeof search.from === 'string' && DATE.test(search.from)) out.from = search.from
  if (typeof search.to === 'string' && DATE.test(search.to)) out.to = search.to
  return out
}

export interface PlatformAuditSearch extends PageSearch {
  restaurant?: string
}

export function parsePlatformAuditSearch(search: Record<string, unknown>): PlatformAuditSearch {
  const out: PlatformAuditSearch = parsePageSearch(search)
  if (typeof search.restaurant === 'string' && search.restaurant) out.restaurant = search.restaurant
  return out
}

/** Local midnight of a `YYYY-MM-DD` day (plus `days`), as the instant the API compares against. */
function dayStart(date: string, days = 0): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y!, m! - 1, d! + days).toISOString()
}

/** The query string for a restaurant page read; `to` is inclusive on the page, exclusive on the wire. */
export function auditQueryString({ branch, entity, from, to }: AuditFilter, page: number): string {
  const params = new URLSearchParams({ page: String(page), per_page: String(PAGE_SIZE) })
  if (branch) params.set('branch_id', branch)
  if (entity) params.set('entity_type', entity)
  if (from) params.set('from', dayStart(from))
  if (to) params.set('to', dayStart(to, 1))
  return params.toString()
}

export const auditKeys = {
  restaurant: (id: string) => ['audit-log', 'restaurant', id] as const,
  restaurantList: (id: string, query: string) => ['audit-log', 'restaurant', id, 'list', query] as const,
  orderHistory: (id: string, branchId: string, orderId: string) => ['audit-log', 'order', id, branchId, orderId] as const,
  platformList: (restaurant: string | undefined, page: number) => ['audit-log', 'platform', restaurant ?? '', page] as const,
}

export function restaurantAuditLogQuery(id: string, filter: AuditFilter, page: number) {
  const query = auditQueryString(filter, page)
  return queryOptions({
    queryKey: auditKeys.restaurantList(id, query),
    queryFn: () => request<AuditLogListResponse>(`/restaurants/${id}/audit-log?${query}`),
  })
}

export function orderHistoryQuery(id: string, branchId: string, orderId: string) {
  return queryOptions({
    queryKey: auditKeys.orderHistory(id, branchId, orderId),
    queryFn: () => request<OrderHistoryResponse>(`/restaurants/${id}/branches/${branchId}/orders/${orderId}/history`),
  })
}

export function platformAuditLogQuery(restaurant: string | undefined, page: number) {
  const params = new URLSearchParams({ page: String(page), per_page: String(PAGE_SIZE) })
  if (restaurant) params.set('restaurant_id', restaurant)
  return queryOptions({
    queryKey: auditKeys.platformList(restaurant, page),
    queryFn: () => request<AuditLogListResponse>(`/platform/audit-log?${params}`),
  })
}
